import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gestione`;
const LS_ACCESS = "gest_access";
const LS_REFRESH = "gest_refresh";

const GestAuthContext = createContext(null);

// Bearer-token store (localStorage + memory) — parallel fallback to
// httpOnly cookies, needed because some in-app browsers (WebViews used by
// mobile e-mail apps, Emergent's own container, Instagram/Facebook browsers…)
// silently drop SameSite=None cookies. Cookies remain the primary transport;
// Bearer only kicks in when they don't.
const tokenStore = {
  get access() { try { return localStorage.getItem(LS_ACCESS) || ""; } catch { return ""; } },
  get refresh() { try { return localStorage.getItem(LS_REFRESH) || ""; } catch { return ""; } },
  save(access, refresh) {
    try {
      if (access) localStorage.setItem(LS_ACCESS, access);
      if (refresh) localStorage.setItem(LS_REFRESH, refresh);
    } catch (_) {}
  },
  clear() {
    try { localStorage.removeItem(LS_ACCESS); localStorage.removeItem(LS_REFRESH); } catch (_) {}
  },
};

export const gestApi = axios.create({ baseURL: API, withCredentials: true });

// Attach Authorization header on every request when we have a token.
gestApi.interceptors.request.use((cfg) => {
  const t = tokenStore.access;
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

// Silent-refresh interceptor.
let refreshInFlight = null;
gestApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && !original.__isRetry && !isAuthEndpoint) {
      original.__isRetry = true;
      try {
        if (!refreshInFlight) {
          const headers = {};
          const rt = tokenStore.refresh;
          if (rt) headers["X-Refresh-Token"] = rt;
          refreshInFlight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, headers }).then((r) => {
            const { access_token, refresh_token } = r.data || {};
            if (access_token) tokenStore.save(access_token, refresh_token || rt);
            return r;
          });
        }
        await refreshInFlight;
        refreshInFlight = null;
        return gestApi.request(original);
      } catch (e) {
        refreshInFlight = null;
        tokenStore.clear();
        try { window.dispatchEvent(new CustomEvent("gest:session-expired")); } catch (_) {}
      }
    }
    return Promise.reject(error);
  }
);

export function GestAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await gestApi.get(`/auth/me`);
      setUser(data);
      return data;
    } catch (_) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => setUser(false);
    window.addEventListener("gest:session-expired", handler);
    return () => window.removeEventListener("gest:session-expired", handler);
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await gestApi.post(`/auth/login`, { email, password });
      // Persist tokens for Bearer fallback (in case cookies are dropped).
      if (data?.access_token) tokenStore.save(data.access_token, data.refresh_token);
      const { access_token, refresh_token, ...userData } = data || {};
      setUser(userData);
      return userData;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Errore di accesso";
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await gestApi.post(`/auth/logout`, {}); } catch (_) {}
    tokenStore.clear();
    setUser(false);
  };

  return (
    <GestAuthContext.Provider value={{
      user, error, login, logout, refresh,
      isAdmin: user?.role === "admin",
      isOffice: user?.role === "ufficio",
      canSeeMoney: user?.role === "admin" || user?.role === "ufficio",
    }}>
      {children}
    </GestAuthContext.Provider>
  );
}

export const useGestAuth = () => useContext(GestAuthContext);
